import type { ReactNode } from 'react';

/**
 * Habillage commun des tableaux de liste.
 *
 * ── Pourquoi ce fichier existe ───────────────────────────────────────────
 * Même raison que `field-shell.ts` pour les champs de formulaire, et même
 * histoire : dix tableaux avaient recopié les mêmes classes, et avaient
 * divergé. Trois d'entre eux étaient même passés au CSS dédié, avec des
 * couleurs écrites en dur (`#faf9ff`, `#fafbfe`) et une variable
 * `--color-navy-25` qui n'existe nulle part.
 *
 * Le plus visible à l'écran était la graisse de l'identifiant : 600 chez les
 * candidats, 500 ailleurs, et les deux dans le même tableau côté CRA. Le nom
 * cessait d'accrocher l'œil, et la ligne devenait un bloc de texte gris.
 *
 * ── Ce que ce fichier n'est pas ──────────────────────────────────────────
 * Pas un composant `DataTable`. Les cellules de ces tableaux sont trop
 * différentes — visages, jauges d'activité, matrices de permissions, étoiles —
 * pour qu'une abstraction les couvre sans se payer en options. Chaque tableau
 * garde donc sa structure et ses colonnes ; seules les valeurs sont communes.
 *
 * ── Ce qui reste au tableau ──────────────────────────────────────────────
 * Sa largeur minimale (`min-w-[…]`), ses colonnes, son `<caption>`. Elles
 * dépendent du contenu : imposer une largeur unique casserait celui qui a neuf
 * colonnes ou celui qui en a trois.
 */

/** Conteneur : le défilement horizontal appartient au tableau, pas à la page. */
export const TABLE_SCROLL = 'overflow-x-auto';

/** Base du `<table>`. La largeur minimale reste propre à chaque tableau. */
export const TABLE = 'w-full border-collapse text-left';

/**
 * En-tête **collant**.
 *
 * Ces tableaux défilent presque tous horizontalement, et verticalement sur
 * vingt-cinq lignes. Un en-tête qui s'échappe emporte les intitulés au moment
 * précis où on en a besoin.
 */
export const TABLE_HEAD = 'bg-navy-50 border-navy-100 sticky top-0 z-10 border-b';

/**
 * Ligne d'en-tête.
 *
 * `font-medium` et non `font-semibold` : les intitulés de colonnes doivent se
 * distinguer des données sans les dominer. C'est le pendant du 600 porté par
 * l'identifiant de chaque ligne — deux graisses, deux rôles.
 */
export const TABLE_HEAD_ROW = 'text-navy-600 text-xs font-medium';

export const TABLE_HEAD_CELL = 'px-3 py-3';
export const TABLE_HEAD_CELL_NOWRAP = 'px-3 py-3 whitespace-nowrap';
export const TABLE_HEAD_CELL_RIGHT = 'px-3 py-3 text-right whitespace-nowrap';
export const TABLE_HEAD_CELL_CENTER = 'px-3 py-3 text-center';

export const TABLE_BODY = 'divide-navy-100 divide-y';

export const TABLE_ROW = 'hover:bg-navy-50/50 transition-colors';

/**
 * Ligne sélectionnée.
 *
 * Elle se teinte : sur vingt-cinq lignes, la seule case cochée en bord de
 * tableau se repère mal.
 */
export const TABLE_ROW_SELECTED = 'bg-accent-50/50 transition-colors';

/** Classe de ligne, selon l'état de sélection. */
export const tableRow = (selected = false): string => (selected ? TABLE_ROW_SELECTED : TABLE_ROW);

export const TABLE_CELL = 'px-3 py-3.5';

/** Cellule de texte courant. `text-sm`, la taille de référence des listes. */
export const TABLE_CELL_TEXT = 'text-ink-600 px-3 py-3.5 text-sm';

/** Cellule de valeur chiffrée : alignée à droite, chiffres à largeur fixe. */
export const TABLE_CELL_NUMERIC = 'text-ink-600 px-3 py-3.5 text-right text-sm tabular-nums';

/**
 * Identifiant principal de la ligne — un vrai lien.
 *
 * `font-semibold` (600) : c'est lui qui structure le balayage vertical. C'est
 * aussi la seule valeur de ce fichier qu'on avait affaiblie à 500 dans quatre
 * tableaux sur six, chaque fois par copier-coller d'une classe dont un seul
 * mot avait changé.
 *
 * Un lien et non une ligne cliquable en JavaScript : ouvrable dans un nouvel
 * onglet, et atteignable au clavier.
 */
export const TABLE_LINK =
  'text-navy-900 hover:text-accent-700 focus-visible:ring-accent-700/25 rounded text-sm font-semibold hover:underline focus-visible:ring-2 focus-visible:outline-none';

/**
 * Variante en bloc et tronquée, quand le lien occupe toute la largeur de sa
 * cellule.
 *
 * `truncate` reste ici et non dans `TABLE_LINK` : il exige un parent à largeur
 * bornée, et l'imposer partout couperait les libellés des tableaux qui
 * laissent leur colonne s'étendre.
 */
export const TABLE_LINK_BLOCK = `block truncate ${TABLE_LINK}`;

/**
 * Identifiant principal qui n'est pas un lien.
 *
 * Même graisse que `TABLE_LINK`, sans l'interaction : certaines lignes n'ont
 * pas de fiche à ouvrir, et leur libellé doit malgré tout accrocher l'œil au
 * même titre que les autres.
 */
export const TABLE_IDENTITY = 'text-navy-900 text-sm font-semibold';

/** Ligne secondaire sous l'identifiant : référence, intitulé, client… */
export const TABLE_SECONDARY = 'text-ink-500 mt-0.5 text-xs';

/**
 * Cellule vide : un tiret cadratin plutôt qu'un blanc ambigu.
 *
 * Une constante et non un composant : ce fichier n'exporte aucun composant, ce
 * qui évite l'avertissement `react-refresh/only-export-components` qu'un
 * mélange des deux déclenche. Un élément React est immuable, le réutiliser
 * partout est sans risque.
 */
export const EMPTY: ReactNode = <span className="text-ink-300">—</span>;

/**
 * Nom affichable d'un utilisateur, avec repli sur son adresse.
 *
 * Ici plutôt que dans `UserCell` : ce fichier n'exporte aucun composant, ce qui
 * laisse le rafraîchissement à chaud de Vite fonctionner. Un fichier qui mêle
 * composants et fonctions le casse, et l'avertissement
 * `react-refresh/only-export-components` est là pour le rappeler.
 */
export const userDisplayName = (user: {
  firstName: string;
  lastName: string;
  email: string;
}): string => [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email;
